import { useEffect, useMemo, useRef, useState } from 'react';
import { getAccessToken } from '../utils/auth';
import { getTrackPointsByHex } from '../utils/httpApi';
import { trimToLanding } from '../utils/landingTrack';

const REFRESH_MS = 30 * 1000;

/**
 * The selected row's aircraft details (for AircraftInfoPopup) and its landing + ground track,
 * trimmed to 2 km before touchdown. `query` comes from arrivalTrackQuery / onGroundTrackQuery /
 * departureTrackQuery, or null when nothing is selected. Fetched when the query's key changes and
 * every 30 s while selected, so the line keeps up with an aircraft still taxiing; the end of the
 * window is always "now". `popupOpen` lets the popup be closed without dropping the selection.
 */
export default function useLandingTrack(query) {
  const key = query?.key || null;
  const queryRef = useRef(query);
  queryRef.current = query;
  const [trackData, setTrackData] = useState(null);
  const [popupOpen, setPopupOpen] = useState(false);

  useEffect(() => {
    setTrackData(null);
    setPopupOpen(!!key);
    if (!key) return undefined;
    let cancelled = false;
    const load = async () => {
      const q = queryRef.current;
      if (!q || q.key !== key) return;
      try {
        const resp = await getTrackPointsByHex(
          { icao24: q.icao24, startTime: q.startTime, callsign: q.callsign, intervalSeconds: 5 },
          getAccessToken());
        if (cancelled) return;
        const ok = resp?.header?.apiStatus === 'OK' || resp?.header?.apiStatus === 'SUCCESS';
        if (ok) setTrackData(resp);
        else console.warn('[useLandingTrack] getTrackPoints failed for', q.icao24, resp?.header?.errors);
      } catch (e) {
        if (!cancelled) console.warn('[useLandingTrack] getTrackPoints error for', q.icao24, e);
      }
    };
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [key]);

  const positions = useMemo(() => {
    const q = queryRef.current;
    return trimToLanding(trackData?.points, { touchdownAt: q?.touchdownAt, last: q?.last });
  }, [trackData]);

  return { trackData, positions, popupOpen, closePopup: () => setPopupOpen(false) };
}
